import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckCircle2,
  ChevronDown,
  FileWarning,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { useIsMobile } from '../../helpers/useIsMobile';
import {
  EadinStatusBadge,
  ReceivingStatusBadge,
  SlimBoxProgress,
  VarianceDot,
} from './receivingAtoms';
import { canCloseReceipt, useTouchLayout } from './receivingLayout';
import {
  resolveShipmentKey,
  shipmentGroupsForPo,
  shipmentUnreceivedUnits,
} from './skuReceiveModel';
import PoInspector from './PoInspector';
import { useReceivingStore } from './ReceivingStore';
import {
  TRIAGE,
  boxesResolved,
  deriveReceivingStatus,
  fmtDate,
  hasOpenException,
  relDue,
  unitVariance,
  totalExpected,
  totalReceived,
  TONE_TEXT,
} from './receivingStatus';
import { mapEadinStatus } from './receivedUtils';
import { LoaderSpinner } from '../../components/LoaderSpinner';
import { ConfrimModal } from '../../components/ConfrimModal';
import BulkReceiveWorkspace from './BulkReceiveWorkspace';
import SkuReceiveWorkspace from './SkuReceiveWorkspace';

const STATUS_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
  { value: 'canceled', label: 'Canceled' },
  // { value: 'draft', label: 'Draft' },
];

const RECEIVING_FILTERS = [
  { value: 'all', label: 'All receiving' },
  { value: 'not_started', label: 'Not started' },
  { value: 'in_transit', label: 'In transit' },
  { value: 'receiving', label: 'Receiving' },
  { value: 'received', label: 'Received' },
  { value: 'short', label: 'Short' },
];

// Optional table columns the user can show/hide. PO and Supplier are always shown.
const OPTIONAL_COLUMNS = [
  { id: 'boxes', label: 'Boxes' },
  { id: 'expected', label: 'Expected' },
  { id: 'received', label: 'Received' },
  { id: 'variance', label: 'Var' },
  { id: 'due', label: 'Due' },
  { id: 'warehouse', label: 'Warehouse' },
  { id: 'received_date', label: 'Received date' },
  { id: 'status', label: 'Status' },
];
const DEFAULT_COLUMNS = ['boxes', 'expected', 'received', 'variance', 'due', 'status'];
const COLUMNS_STORAGE_KEY = 'eadin:receiving-columns:v1';

function poHasReadyShipment(po) {
  return shipmentGroupsForPo(po).some(
    (shipment) => shipment.arrival === 'arrived' && shipmentUnreceivedUnits(shipment) > 0,
  );
}

function receivingDate(value) {
  if (!value) return null;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return `${match[2]}/${match[3]}/${match[1]}`;
}

function normalizedPoNumber(value) {
  return String(value || '').trim().replace(/^PO\s*/i, '').replace(/\s+/g, '').toLowerCase();
}

function matchesPoNumber(po, value) {
  const target = normalizedPoNumber(value);
  return [po.id, po.poNumber, `PO ${po.poNumber}`]
    .some((candidate) => normalizedPoNumber(candidate) === target);
}

function nonReadyExplanation(po) {
  const groups = shipmentGroupsForPo(po);
  const number = `PO ${po.poNumber || po.id}`;
  const inTransit = groups.find((shipment) => shipment.arrival === 'in_transit');
  if (inTransit) {
    const eta = receivingDate(inTransit.eta);
    return `${number} hasn't arrived yet, so there is nothing to receive.${eta ? ` ETA ${eta}.` : ''}`;
  }
  if (totalExpected(po) > 0 && totalReceived(po) >= totalExpected(po)) {
    return `${number} is fully received. Nothing left to receive.`;
  }
  return `${number} hasn't shipped yet, so there is nothing to receive.`;
}

function touchPoStatus(po, ready) {
  if (ready) {
    return {
      label: 'Ready to receive',
      tone: 'bg-[#eff6ff] text-[#1d4ed8]',
      dot: 'bg-[#3b82f6]',
    };
  }
  const groups = shipmentGroupsForPo(po);
  if (totalExpected(po) > 0 && totalReceived(po) >= totalExpected(po)) {
    return {
      label: 'Fully received',
      tone: 'bg-[#ecfdf5] text-[#065f46]',
      dot: 'bg-[#10b981]',
    };
  }
  if (groups.some((shipment) => shipment.arrival === 'in_transit')) {
    return {
      label: 'In transit',
      tone: 'bg-[#eff6ff] text-[#1d4ed8]',
      dot: 'bg-[#3b82f6]',
    };
  }
  return {
    label: 'Not shipped',
    tone: 'bg-white text-[#4b5060] ring-1 ring-inset ring-[#ececef]',
    dot: 'bg-white ring-1 ring-inset ring-[#a2a6b3]',
  };
}

function touchShipmentSummary(shipment) {
  const units = `${shipmentUnreceivedUnits(shipment).toLocaleString()} unreceived`;
  if (shipment.arrival === 'not_shipped') return `Not shipped yet · ${units}`;
  const method = shipment.method
    ? `${shipment.method.charAt(0).toUpperCase()}${shipment.method.slice(1).toLowerCase()}`
    : null;
  const prefix = `Shipment ${shipment.number}${method ? ` · ${method}` : ''}`;
  if (shipment.arrival === 'in_transit') {
    const eta = receivingDate(shipment.eta);
    return `${prefix} · ${eta ? `ETA ${eta}` : 'in transit'} · ${units}`;
  }
  const deliveredDate = receivingDate(shipment.deliveredAt);
  return `${prefix} · ${deliveredDate ? `arrived ${deliveredDate}` : 'arrived'} · ${units}`;
}

function TouchPoList({
  pos,
  query,
  expandedPoId,
  onOpenPo,
  onToggleExplanation,
}) {
  const normalizedQuery = query.trim().toLowerCase();
  const visiblePos = pos.filter((po) => {
    if (!normalizedQuery) return true;
    const tracking = shipmentGroupsForPo(po).map((shipment) => shipment.tracking || '').join(' ');
    return `${po.id} ${po.poNumber} ${po.supplier || ''} ${tracking}`
      .toLowerCase()
      .includes(normalizedQuery);
  });
  const readyPos = visiblePos.filter(poHasReadyShipment);
  const otherPos = visiblePos.filter((po) => !poHasReadyShipment(po));

  const renderRow = (po, ready) => {
    const shipments = shipmentGroupsForPo(po);
    const status = touchPoStatus(po, ready);
    const expanded = expandedPoId === po.id;
    return (
      <div key={po.id} className="border-b border-gray-200 bg-white last:border-b-0">
        <button
          type="button"
          onClick={() => (ready ? onOpenPo(po) : onToggleExplanation(expanded ? null : po.id))}
          aria-expanded={ready ? undefined : expanded}
          className="block w-full px-4 py-3 text-left active:bg-gray-50"
        >
          <div className="flex min-w-0 items-start justify-between gap-3">
            <div className="min-w-0 truncate text-sm font-semibold text-gray-900">
              PO {po.poNumber || po.id} · {po.supplier || 'Unknown vendor'}
            </div>
            <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-[4px] px-2 py-1 text-[11px] font-medium ${status.tone}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
              {status.label}
            </span>
          </div>
          <div className="mt-2 space-y-1">
            {shipments.map((shipment) => {
              return (
                <div key={shipment.key} className="truncate text-xs text-gray-600">
                  {touchShipmentSummary(shipment)}
                </div>
              );
            })}
          </div>
        </button>
        {!ready && expanded && (
          <div className="border-t border-gray-100 bg-gray-50 px-4 py-2.5 text-sm text-gray-700">
            {nonReadyExplanation(po)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-full bg-white">
      {readyPos.length > 0 && (
        <section aria-label="Ready to receive">
          <h2 className="sticky top-0 z-[1] border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs font-bold uppercase tracking-wide text-gray-600">
            READY TO RECEIVE {readyPos.length}
          </h2>
          {readyPos.map((po) => renderRow(po, true))}
        </section>
      )}
      {otherPos.length > 0 && (
        <section aria-label="Everything else">
          <h2 className="sticky top-0 z-[1] border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs font-bold uppercase tracking-wide text-gray-600">
            EVERYTHING ELSE {otherPos.length}
          </h2>
          {otherPos.map((po) => renderRow(po, false))}
        </section>
      )}
      {!visiblePos.length && (
        <div className="px-4 py-12 text-center text-sm text-gray-500">No POs match this search.</div>
      )}
    </div>
  );
}

function ColumnsMenu({ visible, onToggle, onReset }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 text-sm text-gray-600 transition-colors hover:text-gray-900"
      >
        <SlidersHorizontal className="h-3.5 w-3.5" />
        Columns
      </button>
      {open && (
        <div className="absolute right-0 top-full z-40 mt-1 w-52 overflow-hidden rounded-md border border-gray-200 bg-white p-1 shadow-lg">
          {OPTIONAL_COLUMNS.map((col) => (
            <label
              key={col.id}
              className="flex cursor-pointer items-center gap-2 rounded-sm px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              <input
                type="checkbox"
                checked={visible.includes(col.id)}
                onChange={() => onToggle(col.id)}
                className="size-3.5"
              />
              {col.label}
            </label>
          ))}
          <div className="mt-1 border-t border-gray-100 pt-1">
            <button
              type="button"
              onClick={() => {
                onReset();
                setOpen(false);
              }}
              className="w-full rounded-sm px-2.5 py-1.5 text-left text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-800"
            >
              Reset to default
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Toolbar filter pill with attached popover. Active pills render dark with an inline clear button.
function FilterPill({ label, value, displayValue, onClear, options, onSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const active = value !== 'all';

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex h-8 shrink-0 items-center gap-1 rounded-md border px-3 text-sm transition-colors ${
          active
            ? 'border-transparent bg-gray-900 text-white'
            : 'border-gray-300 bg-white text-gray-500 hover:text-gray-900'
        }`}
      >
        {active ? (
          <>
            <span className="max-w-[140px] truncate">{displayValue}</span>
            <span
              role="button"
              aria-label={`Clear ${label}`}
              className="ml-0.5 rounded-sm p-0.5 hover:bg-white/20"
              onClick={(e) => {
                e.stopPropagation();
                onClear();
                setOpen(false);
              }}
            >
              <X className="h-3 w-3" />
            </span>
          </>
        ) : (
          <>
            {label}
            <ChevronDown className="h-3 w-3 opacity-60" />
          </>
        )}
      </button>
      {open && (
        <div className="absolute left-0 top-full z-40 mt-1 max-h-72 w-auto min-w-[160px] overflow-y-auto rounded-md border border-gray-200 bg-white p-1 shadow-lg">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`flex w-full items-center whitespace-nowrap rounded-sm px-2.5 py-1.5 text-left text-sm transition-colors hover:bg-gray-50 ${
                value === opt.value ? 'font-medium text-gray-900' : 'text-gray-700'
              }`}
              onClick={() => {
                onSelect(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ReceivingQueueTab({ onFilteredCountChange, refreshKey = 0 }) {
  const { pos, loading, refreshing, loadQueue, closeReceipt, pagination, ensurePoLoaded } =
    useReceivingStore();
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.currentUser);
  const canClose = canCloseReceipt(currentUser);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [receivingFilter, setReceivingFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [triage, setTriage] = useState(null);
  const [visibleCols, setVisibleCols] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_COLUMNS;
    try {
      const raw = window.localStorage.getItem(COLUMNS_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed)) return parsed.filter((id) => OPTIONAL_COLUMNS.some((c) => c.id === id));
    } catch (err) {
      /* noop */
    }
    return DEFAULT_COLUMNS;
  });
  const showCol = (id) => visibleCols.includes(id);
  const toggleCol = (id) => {
    setVisibleCols((prev) => {
      const next = prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id];
      try {
        window.localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(next));
      } catch (err) {
        /* noop */
      }
      return next;
    });
  };
  const resetCols = () => {
    setVisibleCols(DEFAULT_COLUMNS);
    try {
      window.localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(DEFAULT_COLUMNS));
    } catch (err) {
      /* noop */
    }
  };
  const [selected, setSelected] = useState(new Set());
  const [openPo, setOpenPo] = useState(null);
  const [workspacePoId, setWorkspacePoId] = useState(null);
  const [workspaceShipmentKey, setWorkspaceShipmentKey] = useState(null);
  const [workspaceMode, setWorkspaceMode] = useState('type');
  const [expandedNonReadyPo, setExpandedNonReadyPo] = useState(null);
  const [deepLinkNotFound, setDeepLinkNotFound] = useState('');
  const handledDeepLink = useRef('');

  useEffect(() => {
    if (!openPo) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpenPo(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openPo]);
  const [sortKey, setSortKey] = useState('po');
  const [sortDir, setSortDir] = useState(-1);
  const [showClosed, setShowClosed] = useState(false);
  const touchLayout = useTouchLayout();
  const isMobile = useIsMobile();
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [workspacePoIds, setWorkspacePoIds] = useState(null);

  useEffect(() => {
    const delay = query.trim() ? 300 : 0;
    const handle = setTimeout(() => {
      loadQueue({
        search: query,
        searchTracking: touchLayout,
        statusFilter,
        showClosed,
        resetExisting: true,
      });
    }, delay);
    return () => clearTimeout(handle);
  }, [query, statusFilter, showClosed, loadQueue, refreshKey, touchLayout]);

  const openDeepLinkedPo = (po, params) => {
    const shipmentParam = params.get('shipment');
    const knownShipmentKey = resolveShipmentKey(po, shipmentParam);
    if (touchLayout) {
      setOpenPo(null);
      setWorkspacePoId(po.id);
      setWorkspaceShipmentKey(knownShipmentKey);
      setWorkspaceMode(params.get('mode') === 'scan' ? 'scan' : 'type');
    } else {
      setOpenPo(po.id);
    }
    setDeepLinkNotFound('');
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const poParam = params.get('po');
    if (!poParam) {
      handledDeepLink.current = '';
      setDeepLinkNotFound('');
      return;
    }
    if (handledDeepLink.current === poParam) return;

    const existingPo = pos.find((po) => matchesPoNumber(po, poParam));
    if (existingPo) {
      handledDeepLink.current = poParam;
      openDeepLinkedPo(existingPo, params);
      return;
    }
    if (loading) return;

    handledDeepLink.current = poParam;
    ensurePoLoaded(poParam).then((loadedPo) => {
      const match = [loadedPo, ...pos].find((po) => po && matchesPoNumber(po, poParam));
      if (match) openDeepLinkedPo(match, params);
      else setDeepLinkNotFound(`PO ${poParam} not found`);
    });
  }, [location.search, loading, pos, ensurePoLoaded, touchLayout]);

  const setSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 1 ? -1 : 1));
    else {
      setSortKey(key);
      setSortDir(1);
    }
  };

  const suppliers = useMemo(
    () => Array.from(new Set(pos.map((p) => p.supplier))).sort(),
    [pos],
  );

  const filtered = useMemo(() => {
    let list = pos.filter((po) => {
      if (stageFilter !== 'all' && po.status !== stageFilter) return false;
      if (receivingFilter !== 'all') {
        const closedLike =
          po.eadinStatus === 'closed' ||
          po.eadinStatus === 'canceled' ||
          po.eadinStatus === 'draft' ||
          mapEadinStatus(po.eadinStatus) === 'closed';
        if (closedLike || deriveReceivingStatus(po) !== receivingFilter) return false;
      }
      if (supplierFilter !== 'all' && po.supplier !== supplierFilter) return false;
      if (triage) {
        const t = TRIAGE.find((x) => x.key === triage);
        if (t && !t.match(po)) return false;
      }
      return true;
    });

    const cmp = (a, b) => {
      switch (sortKey) {
        case 'po':
          return a.poNumber - b.poNumber;
        case 'supplier':
          return a.supplier.localeCompare(b.supplier);
        case 'expected':
          return totalExpected(a) - totalExpected(b);
        case 'received':
          return totalReceived(a) - totalReceived(b);
        case 'variance':
          return unitVariance(a) - unitVariance(b);
        case 'status': {
          const aKey = `${a.eadinStatus}-${a.status}`;
          const bKey = `${b.eadinStatus}-${b.status}`;
          return aKey.localeCompare(bKey);
        }
        case 'due':
        default:
          return (a.expectedDate || '').localeCompare(b.expectedDate || '');
      }
    };
    return [...list].sort((a, b) => sortDir * cmp(a, b));
  }, [pos, stageFilter, receivingFilter, supplierFilter, triage, sortKey, sortDir]);

  useEffect(() => {
    onFilteredCountChange?.(filtered.length);
  }, [filtered.length, onFilteredCountChange]);

  const allSelected = filtered.length > 0 && filtered.every((p) => selected.has(p.id));

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(filtered.map((p) => p.id)));
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const SortHead = ({ label, k, className = '', align }) => (
    <th
      className={`sticky top-0 z-10 bg-white px-2 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 ${
        align === 'right' ? 'text-right' : 'text-left'
      } ${className}`}
    >
      <button
        type="button"
        className={`inline-flex items-center gap-1 hover:text-gray-900 ${align === 'right' ? 'flex-row-reverse' : ''}`}
        onClick={() => setSort(k)}
      >
        {label}
        {sortKey === k ? (
          sortDir === 1 ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </button>
    </th>
  );

  const anyFilterActive =
    statusFilter !== 'all' ||
    stageFilter !== 'all' ||
    receivingFilter !== 'all' ||
    supplierFilter !== 'all' ||
    triage !== null ||
    query !== '';

  const clearAllFilters = () => {
    setQuery('');
    setStatusFilter('all');
    setStageFilter('all');
    setReceivingFilter('all');
    setSupplierFilter('all');
    setTriage(null);
  };

  const openPoRow = (po) => {
    if (touchLayout) {
      setOpenPo(null);
      setWorkspacePoId(po.id);
      setWorkspaceShipmentKey(null);
      setWorkspaceMode('type');
      setExpandedNonReadyPo(null);
      return;
    }
    setOpenPo(po.id);
  };

  const closeSkuWorkspace = () => {
    setWorkspacePoId(null);
    setWorkspaceShipmentKey(null);
    setWorkspaceMode('type');
    if (location.search) navigate(location.pathname, { replace: true });
  };

  return (
    <div className="flex min-h-0 flex-1">
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="border-b border-gray-200 bg-white">
          <div className={isMobile ? 'flex flex-col gap-0 px-3 py-2' : 'flex flex-wrap items-center gap-2 px-5 py-2.5'}>
            <div className={`relative ${isMobile ? 'w-full' : 'w-64'}`}>
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={touchLayout ? 'Search PO, vendor, tracking…' : 'Search PO, supplier, SKU, ASN…'}
                className="h-8 w-full rounded border border-gray-300 pl-8 pr-7 text-base"
              />
              {query && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-gray-400 hover:text-gray-700"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {touchLayout ? null : isMobile ? (
              <div
                className="-mx-3 mt-1.5 flex items-center gap-1.5 overflow-x-auto px-3 pb-1"
                style={{ scrollbarWidth: 'none' }}
              >
                <FilterPill
                  label="Status"
                  value={statusFilter}
                  displayValue={STATUS_FILTERS.find((o) => o.value === statusFilter)?.label}
                  onClear={() => setStatusFilter('all')}
                  options={STATUS_FILTERS.filter((o) => o.value !== 'all')}
                  onSelect={setStatusFilter}
                />
                <FilterPill
                  label="Receiving"
                  value={receivingFilter}
                  displayValue={RECEIVING_FILTERS.find((o) => o.value === receivingFilter)?.label}
                  onClear={() => setReceivingFilter('all')}
                  options={RECEIVING_FILTERS.filter((o) => o.value !== 'all')}
                  onSelect={setReceivingFilter}
                />
                <FilterPill
                  label="Supplier"
                  value={supplierFilter}
                  displayValue={supplierFilter === 'all' ? undefined : supplierFilter}
                  onClear={() => setSupplierFilter('all')}
                  options={suppliers.map((s) => ({ value: s, label: s }))}
                  onSelect={setSupplierFilter}
                />
                <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-sm text-gray-500">
                  <input
                    type="checkbox"
                    checked={showClosed}
                    onChange={(e) => setShowClosed(e.target.checked)}
                    className="size-3.5"
                  />
                  <span className="whitespace-nowrap text-sm">Show closed</span>
                </label>
                {anyFilterActive && (
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="shrink-0 text-sm text-gray-500 hover:text-gray-800"
                  >
                    Clear all
                  </button>
                )}
              </div>
            ) : (
              <>
                <FilterPill
                  label="Status"
                  value={statusFilter}
                  displayValue={STATUS_FILTERS.find((o) => o.value === statusFilter)?.label}
                  onClear={() => setStatusFilter('all')}
                  options={STATUS_FILTERS.filter((o) => o.value !== 'all')}
                  onSelect={setStatusFilter}
                />
                <FilterPill
                  label="Receiving"
                  value={receivingFilter}
                  displayValue={RECEIVING_FILTERS.find((o) => o.value === receivingFilter)?.label}
                  onClear={() => setReceivingFilter('all')}
                  options={RECEIVING_FILTERS.filter((o) => o.value !== 'all')}
                  onSelect={setReceivingFilter}
                />
                <FilterPill
                  label="Supplier"
                  value={supplierFilter}
                  displayValue={supplierFilter === 'all' ? undefined : supplierFilter}
                  onClear={() => setSupplierFilter('all')}
                  options={suppliers.map((s) => ({ value: s, label: s }))}
                  onSelect={setSupplierFilter}
                />
                <button
                  type="button"
                  onClick={() => setShowClosed((v) => !v)}
                  className={`flex h-8 shrink-0 items-center gap-1.5 rounded-md border px-3 text-sm transition-colors ${
                    showClosed
                      ? 'border-transparent bg-gray-900 text-white'
                      : 'border-gray-300 bg-white text-gray-500 hover:text-gray-900'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={showClosed}
                    readOnly
                    className="pointer-events-none size-3.5"
                    tabIndex={-1}
                  />
                  Show closed
                </button>
                {anyFilterActive && (
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="text-sm text-gray-500 hover:text-gray-800"
                  >
                    Clear all
                  </button>
                )}
                <span className="ml-auto text-sm text-gray-500 tabular-nums">
                  {filtered.length} result{filtered.length !== 1 ? 's' : ''}
                  {pagination?.rangeRows ? ` · ${pagination.rangeRows}` : ''}
                </span>
                <ColumnsMenu visible={visibleCols} onToggle={toggleCol} onReset={resetCols} />
              </>
            )}
          </div>
        </div>

        {selected.size > 0 && (
          <div className="flex items-center gap-1 bg-gray-900 px-6 py-3 text-white">
            <span className="mr-1 whitespace-nowrap text-base font-semibold tabular-nums">
              {selected.size} selected
            </span>
            <span className="mx-1.5 h-4 w-px bg-white/25" />
            <button
              type="button"
              onClick={() => {
                setOpenPo(null);
                setWorkspacePoIds(Array.from(selected));
              }}
              className="rounded bg-white px-3 py-1 text-sm font-semibold text-gray-900 hover:bg-white/90"
            >
              Bulk receive
            </button>
            <div className="ml-auto flex items-center gap-1">
              {canClose && (
                <button
                  type="button"
                  onClick={() => setCloseConfirmOpen(true)}
                  className="flex items-center gap-1 rounded bg-white px-3 py-1 text-sm font-semibold text-gray-900 hover:bg-white/90"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Close receipts
                </button>
              )}
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                className="rounded p-1 text-white/70 hover:bg-white/15"
                aria-label="Clear selection"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}

        {refreshing && (
          <div className="flex shrink-0 items-center gap-2 border-b border-gray-100 bg-gray-50 px-5 py-1">
            <span className="inline-flex h-3 w-3 items-center justify-center">
              <LoaderSpinner height='12' width='12' colorSpinner='gray' border='2' type='cron' />
            </span>
            <span className="text-sm text-gray-400">Refreshing…</span>
          </div>
        )}
        {loading && pos.length === 0 ? (
          <div className='flex h-full items-center justify-center'>
            <LoaderSpinner height='80' width='80' colorSpinner='black' border='5' />
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-auto">
            {deepLinkNotFound && (
              <div role="alert" className="border-b border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-800">
                {deepLinkNotFound}
              </div>
            )}
            {filtered.length === 0 ? (
              loading ? (
                <div className='flex h-full items-center justify-center'>
                  <LoaderSpinner height='80' width='80' colorSpinner='black' border='5' />
                </div>
              ) :
              <div className="flex h-full flex-col items-center justify-center gap-2 p-10 text-center">
                {/* <CheckCircle2 className="h-8 w-8 text-gray-300" /> */}
                <p className="text-base font-medium text-gray-900">Queue is clear</p>
                <p className="max-w-xs text-sm text-gray-500">
                  Nothing matches the current filters. Clear filters or check back when the next ASN lands.
                </p>
                {anyFilterActive && (
                  <button
                    type="button"
                    onClick={() => {
                      setQuery('');
                      setStatusFilter('all');
                      setStageFilter('all');
                      setSupplierFilter('all');
                      setTriage(null);
                    }}
                    className="mt-1 h-7 rounded border border-gray-300 px-3 text-sm hover:bg-gray-50"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : touchLayout ? (
              <TouchPoList
                pos={filtered}
                query={query}
                expandedPoId={expandedNonReadyPo}
                onOpenPo={openPoRow}
                onToggleExplanation={setExpandedNonReadyPo}
              />
            ) : isMobile ? (
              <div className="divide-y divide-gray-200 bg-white">
                {filtered.map((po) => {
                  const isSel = selected.has(po.id);
                  const exception = hasOpenException(po);
                  return (
                    <div
                      key={po.id}
                      className={`relative flex items-start gap-3 px-3 py-3 transition-colors active:bg-gray-100 ${
                        isSel ? 'bg-blue-50/80' : ''
                      }`}
                      onClick={() => openPoRow(po)}
                    >
                      <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggleOne(po.id)}
                          aria-label={`Select ${po.id}`}
                          className="size-4 rounded border-gray-300"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                            {po.id}
                            {exception && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-red-600" />}
                            {!po.docsComplete && (
                              <FileWarning className="h-3.5 w-3.5 shrink-0 text-amber-600" aria-label="Docs missing" />
                            )}
                          </span>
                          <span className="text-sm font-medium tabular-nums text-gray-900">
                            {totalReceived(po).toLocaleString()}/{totalExpected(po).toLocaleString()}
                          </span>
                        </div>
                        <div className="mt-1 flex items-center gap-1.5">
                          <EadinStatusBadge status={po.eadinStatus} />
                          {po.eadinStatus !== 'closed' && po.eadinStatus !== 'canceled' && (
                            <ReceivingStatusBadge status={deriveReceivingStatus(po)} />
                          )}
                        </div>
                        <div className="mt-1.5 flex items-center gap-2 text-sm text-gray-500">
                          <span className="truncate">{po.supplier}</span>
                          <span className="shrink-0">·</span>
                          <span className="shrink-0 tabular-nums">{fmtDate(po.expectedDate)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <table
                className={`w-full ${touchLayout ? 'min-w-0' : 'min-w-[760px]'} border-separate border-spacing-0 text-base`}
              >
                <thead>
                  <tr>
                    <th className="sticky top-0 z-10 w-9 bg-white px-2 py-2 pl-6">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <SortHead
                      label="PO"
                      k="po"
                      className={touchLayout ? 'w-[110px]' : 'w-[150px]'}
                    />
                    <SortHead label="Supplier" k="supplier" />
                    {showCol('boxes') && (
                      <th className="sticky top-0 z-10 bg-white px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Boxes
                      </th>
                    )}
                    {showCol('expected') && (
                      <SortHead label="Expected" k="expected" align="right" className="w-[76px]" />
                    )}
                    {showCol('received') && (
                      <SortHead label="Received" k="received" align="right" className="w-[76px]" />
                    )}
                    {showCol('variance') && !touchLayout && (
                      <SortHead label="Var" k="variance" align="right" className="w-[56px]" />
                    )}
                    {showCol('due') && (
                      <SortHead
                        label="Due"
                        k="due"
                        className={`w-[92px]${touchLayout ? ' whitespace-nowrap' : ''}`}
                      />
                    )}
                    {showCol('warehouse') && (
                      <th className="sticky top-0 z-10 bg-white px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Warehouse
                      </th>
                    )}
                    {showCol('received_date') && (
                      <th className="sticky top-0 z-10 bg-white px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 w-[92px]">
                        Received
                      </th>
                    )}
                    {showCol('status') && (
                      <SortHead
                        label="Status"
                        k="status"
                        className={`w-[120px]${touchLayout ? ' whitespace-nowrap' : ''}`}
                      />
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((po) => {
                    const { resolved, total } = boxesResolved(po);
                    const exception = hasOpenException(po);
                    const varVal = unitVariance(po);
                    const due = relDue(po);
                    const isSel = selected.has(po.id);
                    const isClosedLike =
                      po.eadinStatus === 'closed' ||
                      po.eadinStatus === 'canceled' ||
                      po.eadinStatus === 'draft' ||
                      mapEadinStatus(po.eadinStatus) === 'closed';
                    const railTone = exception
                      ? po.status === 'blocked' ||
                        (po.exceptions || []).some((e) => !e.resolved && e.kind === 'duplicate_epc')
                        ? 'bg-red-600'
                        : 'bg-amber-500'
                      : null;

                    return (
                      <tr
                        key={po.id}
                        onClick={() => openPoRow(po)}
                        className={`cursor-pointer transition-colors ${
                          openPo === po.id ? 'bg-blue-50' : isSel ? 'bg-blue-50/80' : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="relative px-2 py-2 align-middle pl-6" onClick={(e) => e.stopPropagation()}>
                          {railTone && <span className={`absolute inset-y-0 left-0 w-1 ${railTone}`} />}
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => toggleOne(po.id)}
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td className="px-2 py-2 align-middle">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium">{po.id}</span>
                            {exception && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-red-600" />}
                            {!po.docsComplete && (
                              <FileWarning className="h-3.5 w-3.5 shrink-0 text-amber-600" aria-label="Docs missing" />
                            )}
                          </div>
                          {po.asnRef && (
                            <div className="max-w-[140px] truncate text-sm text-gray-500">
                              {po.asnRef}
                            </div>
                          )}
                        </td>
                        <td
                          className={`${touchLayout ? 'max-w-[160px]' : 'max-w-[200px]'} px-2 py-2 align-middle`}
                        >
                          <div className="truncate">{po.supplier}</div>
                          <div className="truncate text-sm text-gray-500">
                            {(po.lineItems || [])
                              .map((li) => `${li.productName}${li.variant ? ` ${li.variant}` : ''}`)
                              .join(', ')}
                          </div>
                        </td>
                        {showCol('boxes') && (
                          <td className="px-2 py-2 align-middle">
                            {touchLayout ? (
                              <span className="text-sm text-gray-500 tabular-nums">
                                {resolved}/{total}
                              </span>
                            ) : (
                              <div className="flex items-center gap-2">
                                <SlimBoxProgress boxes={po.boxes} />
                                <span className="text-sm text-gray-500 tabular-nums">
                                  {resolved}/{total}
                                </span>
                              </div>
                            )}
                          </td>
                        )}
                        {showCol('expected') && (
                          <td className="px-2 py-2 text-right align-middle tabular-nums">
                            {totalExpected(po).toLocaleString()}
                          </td>
                        )}
                        {showCol('received') && (
                          <td className="px-2 py-2 text-right align-middle tabular-nums">
                            {totalReceived(po) === 0 ? (
                              <span className="text-gray-400">0</span>
                            ) : (
                              totalReceived(po).toLocaleString()
                            )}
                          </td>
                        )}
                        {showCol('variance') && !touchLayout && (
                          <td className="px-2 py-2 text-right align-middle">
                            <VarianceDot value={varVal} />
                          </td>
                        )}
                        {showCol('due') && (
                          <td className={`px-2 py-2 align-middle${touchLayout ? ' whitespace-nowrap' : ''}`}>
                            <div className="tabular-nums">{fmtDate(po.expectedDate)}</div>
                            <div className={`text-sm ${TONE_TEXT[due.tone]}`}>{due.text}</div>
                          </td>
                        )}
                        {showCol('warehouse') && (
                          <td className="px-2 py-2 align-middle text-sm text-gray-600">
                            {po.warehouse || '—'}
                          </td>
                        )}
                        {showCol('received_date') && (
                          <td className="px-2 py-2 align-middle tabular-nums text-sm text-gray-600">
                            {po.receivedDate ? fmtDate(po.receivedDate) : '—'}
                          </td>
                        )}
                        {showCol('status') && (
                          <td className={`px-2 py-2 align-middle${touchLayout ? ' whitespace-nowrap' : ''}`}>
                            {isClosedLike ? (
                              <EadinStatusBadge status={po.eadinStatus} />
                            ) : (
                              <ReceivingStatusBadge status={deriveReceivingStatus(po)} />
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

      </div>

      {!touchLayout && openPo && (
        <aside className="fixed inset-y-0 right-0 z-50 flex w-1/2 min-w-[560px] flex-col border-l border-gray-200 bg-white shadow-2xl">
          <PoInspector poId={openPo} onClose={() => setOpenPo(null)} />
        </aside>
      )}

      <ConfrimModal
        isOpen={canClose && closeConfirmOpen}
        title="Are you sure you want to close receipts for these POs?"
        confirmButtonTitle="Close receipts"
        isLoading={confirmBusy}
        size="max-w-md"
        submitClick={async () => {
          if (!canClose) return;
          const ids = Array.from(selected);
          setConfirmBusy(true);
          try {
            await closeReceipt(ids);
            toast.success(`${ids.length} receipt(s) closed`, { position: 'bottom-right' });
            setSelected(new Set());
            setCloseConfirmOpen(false);
          } finally {
            setConfirmBusy(false);
          }
        }}
        cancelClick={() => {
          if (confirmBusy) return;
          setCloseConfirmOpen(false);
        }}
      />

      {workspacePoIds !== null && (
        <BulkReceiveWorkspace
          selectedPoIds={workspacePoIds}
          onClose={() => setWorkspacePoIds(null)}
        />
      )}
      {workspacePoId !== null && (
        <SkuReceiveWorkspace
          poId={workspacePoId}
          initialShipmentKey={workspaceShipmentKey}
          initialMode={workspaceMode}
          onClose={closeSkuWorkspace}
        />
      )}
    </div>
  );
}

export default ReceivingQueueTab;
